/**
 * TLC Performance — Speed Optimizations
 * 1. Video lazy loading (preload=none + IntersectionObserver)
 * 2. Prefetch visible collection/product links on hover (instant navigation feel)
 * 3. Passive scroll/touch event listeners (reduces jank)
 * 4. Sitewide image hygiene (priority, lazy loading, decoding, missing alt)
 */
(function () {
  'use strict';

  /* ─── 1. VIDEO LAZY LOADING ─────────────────────────────────────────────── */
  if ('IntersectionObserver' in window) {
    var videoObserver = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var video = entry.target;
        video.querySelectorAll('source[data-src]').forEach(function (source) {
          source.src = source.dataset.src;
          source.removeAttribute('data-src');
        });
        if (video.dataset.src) {
          video.src = video.dataset.src;
          delete video.dataset.src;
        }
        video.preload = 'auto';
        video.load();
        if (video.hasAttribute('autoplay')) {
          video.play().catch(function () {});
        }
        videoObserver.unobserve(video);
      });
    }, { rootMargin: '400px 0px' });

    function observeVideos() {
      document.querySelectorAll('video[data-lazy-video]').forEach(function (v) {
        if (!v.dataset.lazyObserved) {
          v.dataset.lazyObserved = '1';
          videoObserver.observe(v);
        }
      });
    }

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', observeVideos);
    } else {
      observeVideos();
    }

    // Catch videos added after page load (carousels, quickview etc.)
    new MutationObserver(function (mutations) {
      var hasNew = false;
      mutations.forEach(function (m) {
        m.addedNodes.forEach(function (n) { if (n.nodeType === 1) hasNew = true; });
      });
      if (hasNew) observeVideos();
    }).observe(document.documentElement, { childList: true, subtree: true });
  }

  /* ─── 2. LINK PREFETCH ON HOVER / TOUCH ────────────────────────────────── */
  // When a user hovers (desktop) or starts a touch (mobile) on a product/collection
  // link, prefetch that page so it loads instantly on click.
  // Only prefetch same-origin links; skip anchors, external, already-prefetched.
  var connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  var canPrefetch = !connection || (!connection.saveData && !/^(slow-)?2g$/i.test(connection.effectiveType || ''));
  var prefetched = new Set();
  var maxPrefetches = 12;

  function prefetchUrl(url) {
    if (!canPrefetch || !url || prefetched.has(url) || prefetched.size >= maxPrefetches) return;
    try {
      var parsed = new URL(url, location.href);
      if (parsed.origin !== location.origin) return; // skip external
      if (parsed.pathname === location.pathname) return; // skip current page
    } catch (e) { return; }

    prefetched.add(url);
    var addPrefetch = function () {
      var link = document.createElement('link');
      link.rel = 'prefetch';
      link.href = url;
      link.as = 'document';
      document.head.appendChild(link);
    };

    if ('requestIdleCallback' in window) {
      requestIdleCallback(addPrefetch, { timeout: 1200 });
    } else {
      setTimeout(addPrefetch, 120);
    }
  }

  function getProductLink(el) {
    // Walk up to find the nearest <a> that points to a product or collection
    // Guard: e.target may be a Text node in capture-phase listeners — no .closest()
    if (!el || typeof el.closest !== 'function') return null;
    var a = el.closest('a[href]');
    if (!a) return null;
    var href = a.href;
    if (href.includes('/products/') || href.includes('/collections/')) return href;
    return null;
  }

  // Prefetch on mouseenter (desktop — 200ms+ before click)
  document.addEventListener('mouseenter', function (e) {
    var url = getProductLink(e.target);
    if (url) prefetchUrl(url);
  }, { capture: true, passive: true });

  // Keep speculative document prefetch to desktop hover. On mobile, first-tap
  // bandwidth is more valuable for interaction and late-loading media.

  /* ─── 3. PASSIVE WHEEL / TOUCH LISTENERS AUDIT ─────────────────────────── */
  // Force all scroll-related event listeners to be passive so they never block
  // scrolling. This is safe because passive listeners can't call preventDefault.
  (function () {
    var orig = EventTarget.prototype.addEventListener;
    var passiveEvents = new Set(['wheel', 'mousewheel', 'touchmove', 'touchstart']);
    EventTarget.prototype.addEventListener = function (type, fn, opts) {
      if (passiveEvents.has(type)) {
        if (typeof opts === 'boolean') {
          opts = { capture: opts, passive: true };
        } else if (!opts) {
          opts = { passive: true };
        } else if (typeof opts === 'object' && opts.passive !== false) {
          opts = Object.assign({}, opts, { passive: true });
        }
      }
      return orig.call(this, type, fn, opts);
    };
  })();

  /* ─── 4. IMAGE HYGIENE: DESIGN-NEUTRAL LCP + SEO HELPERS ──────────────── */
  function getNearbyText(img) {
    var node = img.closest('product-card, .product-card, .card, article, section, li');
    if (!node) return '';
    var heading = node.querySelector('h1, h2, h3, [class*="title"], [class*="heading"]');
    return heading ? heading.textContent.replace(/\s+/g, ' ').trim() : '';
  }

  function optimiseImages() {
    var images = Array.prototype.slice.call(document.images || []);
    var promoted = 0;
    images.forEach(function (img) {
      if (!img.hasAttribute('decoding')) img.setAttribute('decoding', 'async');
      if (img.closest('header, .header, .shopify-section--header, .tlcf-hero, .tlc-coll-hero__media, .product-gallery')) return;

      var rect = img.getBoundingClientRect();
      var isLikelyAboveFold = rect.top < Math.max(window.innerHeight || 0, 900) && rect.bottom > 0;
      var isContentImage = rect.width >= 180 && rect.height >= 160;

      if (isLikelyAboveFold && isContentImage && promoted < 2) {
        promoted += 1;
        if (!img.hasAttribute('fetchpriority')) img.setAttribute('fetchpriority', 'high');
        if (!img.hasAttribute('loading')) img.setAttribute('loading', 'eager');
      } else if (!img.hasAttribute('loading')) {
        img.setAttribute('loading', 'lazy');
      }

      if (!img.getAttribute('alt')) {
        var fallbackAlt = getNearbyText(img) || document.title.replace(/\s*\|.*$/, '').trim();
        if (fallbackAlt) img.setAttribute('alt', fallbackAlt);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', optimiseImages, { once: true });
  } else {
    optimiseImages();
  }
  window.addEventListener('load', optimiseImages, { once: true, passive: true });

  /* ─── 5. COLLECTION DISCOVERY CHIP COPY ───────────────────────────────── */
  function setDiscoveryGroup(row, label, items) {
    if (!row || !items || !items.length) return;
    row.innerHTML = '<span class="tlc-cg__navlabel">' + label + '</span>' +
      items.map(function (item) {
        return '<a class="tlc-cg__navchip" href="' + item[1] + '">' + item[0] + '</a>';
      }).join('');
  }

  function refreshCollectionDiscoveryChips() {
    var path = window.location.pathname.replace(/\/$/, '');
    var rows = document.querySelectorAll('.tlc-cg__filt--chips .tlc-cg__navrow');
    if (rows.length < 2) return;

    if (path === '/collections/fragrance') {
      setDiscoveryGroup(rows[0], 'Shop by format', [
        ['Body Mist', '/collections/body-mist'],
        ['Hair Mist', '/collections/hair-mist-the-love-co'],
        ['Solid Perfume', '/collections/solid-perfume-the-love-co'],
        ['Eau de Parfum', '/collections/eau-de-parfum']
      ]);
      setDiscoveryGroup(rows[1], 'Shop by scent', [
        ['Japanese Cherry Blossom', '/collections/japanese-cherry-blossom'],
        ['Vanilla', '/collections/warm-vanilla'],
        ['Oud', '/collections/oud'],
        ['Fall in Love', '/collections/fall-in-love'],
        ['Shades of Love', '/collections/shades-of-love']
      ]);
    } else if (path === '/collections/gifts') {
      setDiscoveryGroup(rows[0], 'Shop by budget', [
        ['Under Rs.999', '/collections/gifts-under-999-the-love-co'],
        ['Gift Sets', '/collections/gifts'],
        ['Minis', '/collections/minis-travel'],
        ['Premium Gifts', '/collections/leela']
      ]);
      setDiscoveryGroup(rows[1], 'Shop by recipient', [
        ['For Her', '/collections/women-gifting'],
        ['For Him', '/collections/men-gifting'],
        ['Vanilla Gifts', '/collections/warm-vanilla'],
        ['Oud Gifts', '/collections/oud']
      ]);
    } else if (path === '/collections/body-acne') {
      setDiscoveryGroup(rows[0], 'Shop by concern', [
        ['Body Acne', '/collections/body-acne'],
        ['Back + Chest', '/collections/body-acne'],
        ['KP Texture', '/collections/acne'],
        ['Underarm', '/collections/roll-on-the-love-co']
      ]);
      setDiscoveryGroup(rows[1], 'Shop by routine', [
        ['Cleanse', '/collections/body-wash'],
        ['Treat', '/collections/body-acne'],
        ['Moisturise', '/collections/body-lotion'],
        ['Protect', '/collections/suncare-the-love-co']
      ]);
    }
  }

  function scheduleCollectionDiscoveryRefresh() {
    refreshCollectionDiscoveryChips();
    setTimeout(refreshCollectionDiscoveryChips, 250);
    setTimeout(refreshCollectionDiscoveryChips, 1000);
    setTimeout(refreshCollectionDiscoveryChips, 2500);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scheduleCollectionDiscoveryRefresh, { once: true });
  } else {
    scheduleCollectionDiscoveryRefresh();
  }
  window.addEventListener('load', scheduleCollectionDiscoveryRefresh, { once: true, passive: true });
  window.addEventListener('pageshow', scheduleCollectionDiscoveryRefresh, { passive: true });
  if (window.MutationObserver && window.location.pathname.indexOf('/collections/') === 0) {
    var chipRefreshQueued = false;
    new MutationObserver(function () {
      if (chipRefreshQueued) return;
      chipRefreshQueued = true;
      setTimeout(function () {
        chipRefreshQueued = false;
        refreshCollectionDiscoveryChips();
      }, 80);
    }).observe(document.documentElement, { childList: true, subtree: true });
  }

  /* ─── 6. PDP FAQ CONTENT REPAIR ──────────────────────────────────────── */
  var pdpFaqCopy = {
    'active-5-aha-1-bha-body-cleanser': [
      ['How often should I use it?', 'Start 2-3 times a week, then build slowly if your skin stays comfortable.'],
      ['What does it help with?', 'Stubborn KP, strawberry skin, rough texture and pore congestion.'],
      ['Do I need SPF?', 'Yes. Exfoliating acids can increase sun sensitivity, so use SPF on exposed skin.'],
      ['Will it dry out my skin?', 'It can feel strong if overused. Follow with a plain body lotion and reduce frequency if skin feels tight.'],
      ['Is it suitable for sensitive skin?', 'Patch-test first. Sensitive skin should start once a week.'],
      ["What if it's not for me?", 'Pause if irritation continues, then restart less often. COD is available and shipping is free over Rs.599.']
    ],
    'active-2-salicylic-acid-body-cleanser': [
      ['How often should I use it?', 'Start 3-4 times a week. Use daily only if your skin stays comfortable.'],
      ['What does it help with?', 'Back, chest and shoulder breakouts, oily congestion and clogged pores.'],
      ['Do I need SPF?', 'Use SPF on exposed body areas, especially if you also use other actives.'],
      ['Will it dry out my skin?', 'It is built for acne-prone body skin, but reduce frequency if dryness appears.'],
      ['Is it suitable for sensitive skin?', 'Patch-test first and avoid using it on broken or freshly shaved skin.'],
      ["What if it's not for me?", 'If stinging or dryness continues, pause use and restart less often. COD is available and shipping is free over Rs.599.']
    ],
    'active-3-vitamin-c-body-cleanser': [
      ['How often should I use it?', 'Use daily if your skin is comfortable, or alternate days if you are new to actives.'],
      ['What does it help with?', 'Dull, uneven-looking body skin and a fresher shower routine.'],
      ['Do I need SPF?', 'Yes. Keep SPF in the routine when treating dullness or uneven tone.'],
      ['Will it dry out my skin?', 'It should feel comfortable as a cleanser. Moisturise after showering if skin feels dry.'],
      ['Is it suitable for sensitive skin?', 'Patch-test first if your skin is reactive.'],
      ["What if it's not for me?", 'Reduce frequency if irritation appears. COD is available and shipping is free over Rs.599.']
    ],
    '1-salicylic-acid-body-wash-gentle-exfoliation-clear-skin': [
      ['How often should I use it?', 'Start daily if your skin is comfortable, or every other shower if you are dry or sensitive.'],
      ['What does it help with?', 'Mild body breakouts, clogged pores and rough active-prone areas.'],
      ['Do I need SPF?', 'Use SPF on exposed areas if you are also treating marks or using other actives.'],
      ['Will it dry out my skin?', 'It is the gentler salicylic option, but moisturise after if skin feels tight.'],
      ['Is it suitable for sensitive skin?', 'Patch-test first and avoid broken skin.'],
      ["What if it's not for me?", 'If dryness appears, use it every other shower. COD is available and shipping is free over Rs.599.']
    ],
    'bacne-warrior-acne-fighting-body-spray': [
      ['How long does a bottle last?', 'With daily use on affected areas, one 100ml bottle usually lasts 6-8 weeks.'],
      ['When is the best time to use it?', 'Use after a shower on clean, dry body skin, especially after workouts.'],
      ['Does it work on back, chest and shoulder acne?', 'Yes. The 360 spray is built for back, chest, shoulders and neck.'],
      ['Will it dry out my skin?', '2% salicylic acid is balanced with niacinamide, cica and aloe for comfortable body use.'],
      ['Is it suitable for daily and sensitive skin?', 'Start slowly and patch-test first if your skin is sensitive.'],
      ["What if it's not for me?", 'Use it consistently on clean, dry body skin. If irritation continues, pause use and contact support. COD is available and shipping is free over Rs.599.']
    ]
  };

  function buildPdpFaq(items) {
    return items.map(function (item) {
      return '<div class="qa"><h3>' + item[0] + '</h3><p>' + item[1] + '</p></div>';
    }).join('');
  }

  function repairPdpFaqContent() {
    if (window.location.pathname.indexOf('/products/') !== 0) return;
    var handle = window.location.pathname.replace(/\/$/, '').split('/').pop();
    var items = pdpFaqCopy[handle];
    if (!items) return;
    var faqs = Array.prototype.slice.call(document.querySelectorAll('#bwf .faq'));
    faqs.forEach(function (faq) {
      var text = faq.textContent.replace(/\s+/g, ' ').trim();
      var needsRepair = text.indexOf('Key Benefits') !== -1 ||
        text.indexOf('Timeline') !== -1 ||
        text.indexOf('support if the product arrives damaged or incorrect') !== -1;
      if (needsRepair) faq.innerHTML = buildPdpFaq(items);
    });
  }

  function schedulePdpFaqRepair() {
    repairPdpFaqContent();
    setTimeout(repairPdpFaqContent, 250);
    setTimeout(repairPdpFaqContent, 1000);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', schedulePdpFaqRepair, { once: true });
  } else {
    schedulePdpFaqRepair();
  }
  window.addEventListener('load', schedulePdpFaqRepair, { once: true, passive: true });

  /* ─── 7. ANALYTICS EVENT BRIDGE ───────────────────────────────────────── */
  (function () {
    var ctx = window.tlcAnalyticsContext || {};
    var product = ctx.product || {};
    var isProductPage = ctx.pageType === 'product' || window.location.pathname.indexOf('/products/') === 0;
    var firedDepth = {};

    function handleFromPath() {
      if (product.handle) return product.handle;
      if (window.location.pathname.indexOf('/products/') !== 0) return '';
      return window.location.pathname.replace(/\/$/, '').split('/').pop();
    }

    function selectedVariantId() {
      var input = document.querySelector('form[action*="/cart/add"] input[name="id"], product-form input[name="id"], select[name="id"]');
      return input ? input.value : product.variantId || '';
    }

    function basePayload(extra) {
      var payload = {
        event: '',
        page_type: ctx.pageType || '',
        template: ctx.template || '',
        path: window.location.pathname,
        product_handle: handleFromPath(),
        product_id: product.id || '',
        product_title: product.title || '',
        product_type: product.type || '',
        variant_id: selectedVariantId(),
        price_cents: product.priceCents || '',
        timestamp: Date.now()
      };
      Object.keys(extra || {}).forEach(function (key) {
        payload[key] = extra[key];
      });
      return payload;
    }

    function pushEvent(name, extra) {
      var payload = basePayload(extra);
      payload.event = name;
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(payload);
      try {
        window.dispatchEvent(new CustomEvent('tlc:analytics', { detail: payload }));
      } catch (e) {}
    }

    if (isProductPage) {
      pushEvent('tlc_pdp_view');
    }

    window.addEventListener('scroll', function () {
      if (!isProductPage) return;
      var doc = document.documentElement;
      var scrollable = Math.max(1, doc.scrollHeight - window.innerHeight);
      var depth = Math.round((window.scrollY / scrollable) * 100);
      [25, 50, 75, 90].forEach(function (mark) {
        if (depth >= mark && !firedDepth[mark]) {
          firedDepth[mark] = true;
          pushEvent('tlc_pdp_scroll_depth', { scroll_depth: mark });
        }
      });
    }, { passive: true });

    document.addEventListener('click', function (event) {
      var target = event.target;
      if (!target || !target.closest) return;

      var buyNow = target.closest('#kyoto-buynow, #bwf-buynow, [data-tlc-buy-now], [data-tlcf-buynow], .buybtn--buy, button[name="checkout"]');
      var checkout = target.closest('a[href*="/checkout"], button[name="checkout"], input[name="checkout"]');
      var addToCart = target.closest('form[action*="/cart/add"] button[type="submit"], form[action*="/cart/add"] input[type="submit"], product-form button[type="submit"], button[name="add"], .tlc-atc, .add-to-cart');
      var collectionFilter = target.closest('.tlc-cg__navchip, [data-filter], .facets a, facet-link, a[href*="filter."]');

      if (buyNow) {
        pushEvent('tlc_buy_now_click', { cta_text: buyNow.textContent.replace(/\s+/g, ' ').trim() });
        return;
      }

      if (checkout) {
        pushEvent('tlc_checkout_click', { cta_text: checkout.textContent.replace(/\s+/g, ' ').trim() });
        return;
      }

      if (addToCart && (isProductPage || addToCart.closest('form[action*="/cart/add"], product-form'))) {
        pushEvent('tlc_add_to_cart_click', { cta_text: addToCart.textContent.replace(/\s+/g, ' ').trim() });
        return;
      }

      if (collectionFilter) {
        pushEvent('tlc_collection_filter_click', {
          filter_text: collectionFilter.textContent.replace(/\s+/g, ' ').trim(),
          filter_href: collectionFilter.getAttribute('href') || ''
        });
      }
    }, true);
  })();

  (function initPdpStatutoryFallback() {
    if (window.__tlcPdpStatutoryAssetFallback) return;
    window.__tlcPdpStatutoryAssetFallback = true;

    function isProductPage() {
      return document.body && (
        document.body.classList.contains('template-product') ||
        /\/products\//.test(window.location.pathname)
      );
    }

    function currentMrpText() {
      var el = document.querySelector('.opt.sel .amt, .kyoto-pdp .opt.sel .amt, .kyoto-pdp .amt, [data-product-price], .price-item--regular');
      var text = el && el.textContent ? el.textContent.replace(/\s+/g, ' ').trim() : '';
      return text || 'See selected product price';
    }

    function ensureStyle() {
      if (document.getElementById('tlc-statutory-asset-style')) return;
      var style = document.createElement('style');
      style.id = 'tlc-statutory-asset-style';
      style.textContent = '.kyoto-statutory{position:fixed;inset:0;z-index:260;display:none;align-items:center;justify-content:center;padding:22px}.kyoto-statutory.is-open,.kyoto-statutory.open{display:flex}.kyoto-statutory__overlay{position:absolute;inset:0;background:rgba(35,15,10,.48);backdrop-filter:blur(3px)}.kyoto-statutory__card{position:relative;width:min(560px,100%);max-height:min(760px,calc(100vh - 44px));overflow:auto;background:#fffaf7;border:1px solid #ead8cb;border-radius:18px;padding:32px 34px;box-shadow:0 26px 80px rgba(50,20,12,.22);color:#2b130f}.kyoto-statutory__close{position:absolute;top:16px;right:18px;width:34px;height:34px;border:0;background:transparent;color:#9d8076;font-size:26px;line-height:1;cursor:pointer}.kyoto-statutory h3{margin:0 42px 18px 0;font-size:13px;letter-spacing:.12em;text-transform:uppercase;color:#8c2f4d}.kyoto-statutory dl{margin:0;display:grid;gap:12px}.kyoto-statutory dl div{display:grid;grid-template-columns:128px 1fr;gap:16px}.kyoto-statutory dt{font-size:12px;color:#9d8076;margin:0}.kyoto-statutory dd{font-size:13px;line-height:1.55;color:#2b130f;margin:0}.kyoto-statutory dd span{color:#9d8076}.kyoto-statutory p{margin:20px 0 0;padding-top:16px;border-top:1px solid #ead8cb;font-style:italic;font-size:12px;color:#9d8076;line-height:1.55}@media(max-width:560px){.kyoto-statutory{padding:14px}.kyoto-statutory__card{padding:28px 22px;border-radius:14px}.kyoto-statutory dl div{grid-template-columns:1fr;gap:3px}}';
      document.head.appendChild(style);
    }

    function getModal() {
      var existing = document.getElementById('statutory-modal') ||
        document.getElementById('tap-statutory-modal') ||
        document.getElementById('bb-statutory-modal') ||
        document.getElementById('tlctrv-statutory');
      if (existing) return existing;
      ensureStyle();
      var modal = document.createElement('div');
      modal.className = 'kyoto-statutory modal';
      modal.id = 'statutory-modal';
      modal.setAttribute('aria-hidden', 'true');
      modal.innerHTML = '<div class="kyoto-statutory__overlay" data-statutory-close></div><div class="kyoto-statutory__card" role="dialog" aria-modal="true" aria-labelledby="kyoto-statutory-title"><button type="button" class="kyoto-statutory__close" data-statutory-close aria-label="Close">×</button><h3 id="kyoto-statutory-title">Manufacturer &amp; statutory info</h3><dl><div><dt>Generic name</dt><dd>Cosmetic</dd></div><div><dt>Net quantity</dt><dd>See pack</dd></div><div><dt>M.R.P</dt><dd>' + currentMrpText() + ' <span>(incl. of all taxes)</span></dd></div><div><dt>Mfd. by</dt><dd>Harrods Health Private Limited<br><span>237/6, Jaraut Road, Gurunanak Nagar, Ambala City, Haryana - 134007, India</span></dd></div><div><dt>Mkt. by</dt><dd>Anadoris Health Private Limited</dd></div><div><dt>Country of origin</dt><dd>India</dd></div><div><dt>Mfg. date / Batch</dt><dd>See pack</dd></div><div><dt>Best before</dt><dd>24 months from MFD (unopened) · 6 months after opening</dd></div><div><dt>Customer care</dt><dd><a href="mailto:support@theloveco.in">support@theloveco.in</a> · <a href="tel:+917404055520">+91 74040 55520</a></dd></div></dl><p>In compliance with the Legal Metrology (Packaged Commodities) Rules, 2011.</p></div>';
      document.body.appendChild(modal);
      return modal;
    }

    function setModal(open) {
      var modal = getModal();
      if (!modal) return;
      modal.classList.toggle('is-open', open);
      modal.classList.toggle('open', open);
      modal.setAttribute('aria-hidden', open ? 'false' : 'true');
      document.body.style.overflow = open ? 'hidden' : '';
    }

    document.addEventListener('click', function (event) {
      if (!isProductPage()) return;
      var target = event.target;
      if (!target || !target.closest) return;
      if (target.closest('[data-statutory-open], [data-stat-open], .kyoto-pdp .trustrow .i[title]')) {
        event.preventDefault();
        event.stopPropagation();
        setModal(true);
        return;
      }
      if (target.closest('[data-statutory-close], [data-tap-statutory-close], [data-stat-close], .smodal .close')) {
        event.preventDefault();
        event.stopPropagation();
        setModal(false);
      }
    }, true);

    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape') setModal(false);
    });
  })();

})();
